import Part, { PartType } from '@models/editor/Part'

import { InstrumentManifestType } from './instrument-manifest'
import nfsyn from './nf-webaudio'
import { SampledInstrumentType } from './sampled-instrument'
import { SynthesisContextType } from './synthesis-context'

export interface InstrumentDescriptorType {
  manifest: InstrumentManifestType
  xml: Element
  classic: boolean
  getUri: () => string
  getAliases: () => string[]
  getNameInPartDefaults: () => string | void
  createInstrument: (
    opt_parts: PartType,
    opt_synth?: SynthesisContextType | null
  ) => SampledInstrumentType
  parseSpecification: (xml: Element) => InstrumentSpecificationType
  getSpecifications: () => InstrumentSpecificationType[]
  getName: () => string | null
  getPartDefaults: () => Element
  isUnpitched: () => boolean
  _uri?: string | null
  _aliases?: string[]
  _specifications: InstrumentSpecificationType[]
  _name: string | null
  _unpitched?: boolean
  _partDefaults?: Element
}

export interface InstrumentSpecificationType {
  descriptor: InstrumentDescriptorType
  type: string | null
  location: string | null
  bitsPerSample: number | null
  sampleRate: number | null
  channels: number | null
}

/**
 * InstrumentDescriptor wraps the XML of an instrument in the InstrumentManifest and provides getters to inspect
 * its data.
 *
 * @param manifest InstrumentManifest
 * @param xml XML for the instrument this should describe.
 */
const InstrumentDescriptor = function (
  this: InstrumentDescriptorType,
  manifest: InstrumentManifestType,
  xml: Element
): void {
  this.manifest = manifest

  // We save the instrument's XML so we can lazily parse it if asked for some detail of it.
  this.xml = xml
  this.classic = this.xml.getAttribute('classic') === 'true' ? true : false
}

// opt_part: An optional notation.model.Part object. If provided, and the Part already has an
// instrument cached in its synthesizer field, that instrument is returned. Otherwise a new
// instrument is created, and the Part's synthesizer field is set to be that new instrument.
// opt_synth: An optional synthesizer. If not provided, the manifest synth will be used.
InstrumentDescriptor.prototype.createInstrument = function (
  this: InstrumentDescriptorType,
  opt_part?: PartType,
  opt_synth?: SynthesisContextType
): SampledInstrumentType | null {
  const part = opt_part || new Part()
  const synthesizer = opt_synth ? opt_synth : this.manifest.synth
  part.synthesizer = new nfsyn.SampledInstrument(
    synthesizer,
    this,
    part,
    !!this.manifest.options.preview
  )
  return part.synthesizer
}

InstrumentDescriptor.prototype.getName = function (this: InstrumentDescriptorType) {
  if (typeof this._name === 'undefined') {
    this._name = this.xml.getElementsByTagName('name')[0].textContent
  }
  return this._name
}

InstrumentDescriptor.prototype.getUri = function (this: InstrumentDescriptorType) {
  if (typeof this._uri === 'undefined') {
    this._uri = this.xml.getElementsByTagName('uri')[0].textContent
  }
  return this._uri
}

InstrumentDescriptor.prototype.getAliases = function (this: InstrumentDescriptorType) {
  if (typeof this._aliases === 'undefined') {
    this._aliases = []

    // spread operator converts type HTMLCollectionOf<Element> to Element[]
    // so that it can be looooooped over
    ;[...this.xml.getElementsByTagName('alias')].forEach((element) => {
      if (this._aliases && element.textContent) {
        this._aliases.push(element.textContent)
      }
    })
  }
  return this._aliases
}

InstrumentDescriptor.prototype.getSpecifications = function (
  this: InstrumentDescriptorType
): InstrumentSpecificationType[] {
  if (typeof this._specifications === 'undefined') {
    // spread operator converts type HTMLCollectionOf<Element> to Element[]
    // so that it can be looooooped over
    return [...this.xml.getElementsByTagName('specification')].map((element: Element) =>
      this.parseSpecification(element)
    )
  }
  return this._specifications
}

// /**
//  * For now we'll leave partDefaults as XML, as that's what is expected by the consumer.
//  */
InstrumentDescriptor.prototype.getPartDefaults = function (
  this: InstrumentDescriptorType
): Element {
  if (typeof this._partDefaults === 'undefined') {
    this._partDefaults = this.xml.getElementsByTagName('partDefaults')[0]
  }
  return this._partDefaults
}

/**
 * For now we'll leave partDefaults as XML, as that's what is expected by the consumer.
 */
InstrumentDescriptor.prototype.getNameInPartDefaults = function (): string | void {
  if (typeof this._nameInPartDefaults === 'undefined') {
    this._nameInPartDefaults = this.xml.getElementsByTagName('partDefaults name')[0].textContent
  }
  return this._nameInPartDefaults
}

InstrumentDescriptor.prototype.isUnpitched = function (this: InstrumentDescriptorType): boolean {
  if (typeof this._unpitched === 'undefined') {
    // We use getPartDefaults() to make sure the defaults have been evaluated first.
    const clefSign = this.getPartDefaults().getElementsByTagName('sign')[0].textContent
    // HACK: only current way to determine whether this part is eligible for transposition or not
    this._unpitched = clefSign == 'percussion' || clefSign == 'percussion2'
  }
  return this._unpitched
}

// Parse the specification XML into a JSON document so we can work with it.
InstrumentDescriptor.prototype.parseSpecification = function (
  this: InstrumentDescriptorType,
  xml: Element
): InstrumentSpecificationType {
  return {
    descriptor: this,
    type: xml.getElementsByTagName('type')[0].textContent,
    location: xml.getElementsByTagName('location')[0].textContent,
    bitsPerSample: Number(xml.getElementsByTagName('bitsPerSample')[0].textContent),
    sampleRate: Number(xml.getElementsByTagName('sampleRate')[0].textContent),
    channels: Number(xml.getElementsByTagName('channels')[0].textContent),
  }
}

export default InstrumentDescriptor
